import { useEffect, useRef, useState } from "react";

import type { CollectionDelta, OpaqueId, StreamTopic } from "../contract/types";
import { createSnapshotSync, type StreamClient } from "../api/stream";

/**
 * Colle un instantané REST et le flux temps réel d'un sujet en une liste React
 * toujours à jour. S'appuie sur `createSnapshotSync` : trous de séquence et
 * reconnexions déclenchent un rechargement d'instantané, transparent pour l'écran.
 *
 * `keyOf` extrait l'identité stable d'un élément (pour appliquer upserts/removals).
 */
export interface LiveCollectionState<T> {
  items: T[];
  /** `true` tant que le premier instantané n'est pas arrivé. */
  loading: boolean;
  error: unknown;
  /** Dernier instant de mise à jour (instantané ou delta). */
  updatedAt: Date | null;
  /** Recharge immédiatement l'instantané (ex. après une écriture). */
  refresh: () => void;
}

interface UseLiveCollectionArgs<TItem, TSnapshot> {
  topic: StreamTopic;
  stream: StreamClient | null;
  loadSnapshot: (signal: AbortSignal) => Promise<TSnapshot>;
  selectItems: (snapshot: TSnapshot) => TItem[];
  selectSeq?: (snapshot: TSnapshot) => number | undefined;
  keyOf: (item: TItem) => OpaqueId;
  /** Dépendances qui, si elles changent, forcent un nouvel instantané. */
  deps: ReadonlyArray<unknown>;
  /**
   * Filet de sécurité : recharge l'instantané toutes les `refreshIntervalMs` ms
   * même si le flux SSE paraît vivant. Sert quand le flux se tait sans se couper
   * (positions figées) — le `GET` d'instantané, lui, rend la donnée serveur
   * fraîche. `undefined` ⇒ aucun rechargement périodique (défaut historique).
   */
  refreshIntervalMs?: number;
}

export function useLiveCollection<TItem, TSnapshot>(
  args: UseLiveCollectionArgs<TItem, TSnapshot>,
): LiveCollectionState<TItem> {
  const { topic, stream, loadSnapshot, selectItems, selectSeq, keyOf, deps, refreshIntervalMs } = args;

  const refreshRef = useRef<() => void>(() => {});
  const [state, setState] = useState<LiveCollectionState<TItem>>({
    items: [],
    loading: true,
    error: null,
    updatedAt: null,
    refresh: () => refreshRef.current(),
  });

  // Réfs pour ne pas relancer la synchro à chaque rendu.
  const fns = useRef({ loadSnapshot, selectItems, selectSeq, keyOf });
  fns.current = { loadSnapshot, selectItems, selectSeq, keyOf };

  useEffect(() => {
    if (!stream) return;
    setState((s) => ({ ...s, loading: true, error: null }));

    const sync = createSnapshotSync<Map<OpaqueId, TItem>, CollectionDelta<TItem>>({
      topic,
      stream,
      loadSnapshot: async (signal) => {
        const snapshot = await fns.current.loadSnapshot(signal);
        const map = new Map<OpaqueId, TItem>();
        for (const item of fns.current.selectItems(snapshot)) map.set(fns.current.keyOf(item), item);
        const seq = fns.current.selectSeq?.(snapshot);
        return seq === undefined ? { state: map } : { state: map, seq };
      },
      applyDelta: (map, delta) => {
        const next = new Map(map);
        for (const item of delta.upserts) next.set(fns.current.keyOf(item), item);
        for (const id of delta.removals) next.delete(id);
        return next;
      },
      onState: (map) => {
        setState({
          items: [...map.values()],
          loading: false,
          error: null,
          updatedAt: new Date(),
          refresh: () => refreshRef.current(),
        });
      },
      onError: (error) => setState((s) => ({ ...s, loading: false, error })),
    });

    refreshRef.current = sync.refresh;

    // Filet de sécurité : re-`GET` périodique de l'instantané (flux figé sans
    // coupure ⇒ le delta ne vient jamais, mais l'instantané reste frais).
    const timer =
      refreshIntervalMs && refreshIntervalMs > 0
        ? setInterval(() => refreshRef.current(), refreshIntervalMs)
        : null;

    return () => {
      if (timer) clearInterval(timer);
      sync.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, stream, refreshIntervalMs, ...deps]);

  return state;
}
